import React from 'react';
import { Download } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useFilter } from '../../context/FilterContext';
import { UserRole } from '../../types';

const STATES = ['Gujarat', 'Tamil Nadu', 'Rajasthan', 'Maharashtra', 'Karnataka', 'Madhya Pradesh', 'Kerala'];

const DISTRICTS_BY_STATE: Record<string, string[]> = {
  'Gujarat':         ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Junagadh'],
  'Tamil Nadu':      ['Chennai', 'Coimbatore', 'Madurai'],
  'Rajasthan':       ['Jaipur', 'Jodhpur', 'Ajmer'],
  'Maharashtra':     ['Mumbai', 'Pune', 'Nashik'],
  'Karnataka':       ['Bengaluru', 'Mysuru', 'Hubli'],
  'Madhya Pradesh':  ['Bhopal', 'Indore', 'Jabalpur'],
  'Kerala':          ['Thiruvananthapuram', 'Kochi', 'Kozhikode'],
};

const DISCOMS_BY_STATE: Record<string, string[]> = {
  'Gujarat':         ['PGVCL', 'MGVCL', 'DGVCL', 'UGVCL'],
  'Tamil Nadu':      ['TANGEDCO'],
  'Rajasthan':       ['JVVNL', 'AVVNL', 'JDVVNL'],
  'Maharashtra':     ['MSEDCL'],
  'Karnataka':       ['BESCOM', 'GESCOM', 'HESCOM', 'CESC'],
  'Madhya Pradesh':  ['MPPKVVCL', 'MPMKVVCL'],
  'Kerala':          ['KSEB'],
};

// District → DISCOM narrowing (when both state + district are selected)
const DISCOMS_BY_DISTRICT: Record<string, string[]> = {
  'Ahmedabad':          ['PGVCL'],
  'Surat':              ['DGVCL'],
  'Vadodara':           ['MGVCL'],
  'Rajkot':             ['PGVCL'],
  'Junagadh':           ['PGVCL'],
  'Chennai':            ['TANGEDCO'],
  'Coimbatore':         ['TANGEDCO'],
  'Madurai':            ['TANGEDCO'],
  'Jaipur':             ['JVVNL'],
  'Jodhpur':            ['JDVVNL'],
  'Ajmer':              ['AVVNL'],
  'Mumbai':             ['MSEDCL'],
  'Pune':               ['MSEDCL'],
  'Nashik':             ['MSEDCL'],
  'Bengaluru':          ['BESCOM'],
  'Mysuru':             ['GESCOM'],
  'Hubli':              ['HESCOM'],
  'Bhopal':             ['MPPKVVCL'],
  'Indore':             ['MPMKVVCL'],
  'Jabalpur':           ['MPPKVVCL'],
  'Thiruvananthapuram': ['KSEB'],
  'Kochi':              ['KSEB'],
  'Kozhikode':          ['KSEB'],
};

// DISCOM role: circle → divisions (names match pgvclSubDivMonitoring)
const CIRCLES = ['Ahmedabad', 'Gandhinagar', 'Ankleshwar', 'Kheda', 'Mehsana'];
const DIVISIONS: Record<string, string[]> = {
  'Ahmedabad':   ['North', 'Central', 'South'],
  'Gandhinagar': ['East', 'West'],
  'Ankleshwar':  ['East', 'West'],
  'Kheda':       ['North', 'South'],
  'Mehsana':     ['North', 'South'],
};

// Sub-divisions keyed by "Circle/Division"
const SUB_DIVISIONS: Record<string, string[]> = {
  'Ahmedabad/North':   ['Satellite Road', 'Bopal', 'Chandkheda'],
  'Ahmedabad/Central': ['Maninagar', 'Narol', 'Vatva'],
  'Ahmedabad/South':   ['Sarkhej', 'Sanand', 'Bavla'],
  'Gandhinagar/East':  ['Sector 8', 'Sector 15', 'Mansa'],
  'Gandhinagar/West':  ['Kalol', 'Dehgam', 'Vijapur'],
  'Ankleshwar/East':   ['GIDC Phase 1', 'GIDC Phase 2'],
  'Ankleshwar/West':   ['Zadeshwar', 'Amod'],
  'Kheda/North':       ['Nadiad', 'Anand'],
  'Kheda/South':       ['Petlad', 'Khambhat'],
  'Mehsana/North':     ['Mehsana City', 'Kadi'],
  'Mehsana/South':     ['Visnagar', 'Unjha'],
};

// Vendor role operates in 5 states
const VENDOR_STATES = ['Gujarat', 'Rajasthan', 'Maharashtra', 'Karnataka', 'Tamil Nadu'];

const DATE_PRESETS = [
  { key: 'today',   label: 'Today'          },
  { key: 'week',    label: 'This week'      },
  { key: 'month',   label: 'This month'     },
  { key: '3months', label: 'Last 3 months'  },
] as const;

export default function FilterBar() {
  const { currentRole } = useAuth();
  const {
    datePreset, setDatePreset,
    selectedState, setSelectedState,
    selectedDistrict, setSelectedDistrict,
    selectedDISCOM, setSelectedDISCOM,
    selectedCircle, setSelectedCircle,
    selectedSubDivision, setSelectedSubDivision,
  } = useFilter();

  const isConsumer = currentRole === UserRole.CONSUMER;
  const isDISCOM   = currentRole === UserRole.DISCOM;
  const isVendor   = currentRole === UserRole.VENDOR;
  const isNational = currentRole === UserRole.NATIONAL;
  const isState    = currentRole === UserRole.STATE;

  const selectCls = 'text-xs border border-border rounded-input px-2.5 py-1.5 bg-white text-text-primary focus:outline-none focus:border-accent disabled:opacity-40 disabled:cursor-not-allowed';

  // DISCOM role computed options
  const availableDivisions    = selectedCircle ? (DIVISIONS[selectedCircle] ?? []) : [];
  const availableSubDivisions = (selectedCircle && selectedDISCOM)
    ? (SUB_DIVISIONS[`${selectedCircle}/${selectedDISCOM}`] ?? [])
    : [];

  // National / Vendor: DISCOM narrows to district if district is selected, else all in state
  function resolveDiscomOptions(state: string, district: string): string[] {
    if (!state) return [];
    if (district && DISCOMS_BY_DISTRICT[district]) return DISCOMS_BY_DISTRICT[district];
    return DISCOMS_BY_STATE[state] ?? [];
  }

  const nationalDistricts  = selectedState ? (DISTRICTS_BY_STATE[selectedState] ?? []) : [];
  const nationalDISCOMs    = resolveDiscomOptions(selectedState, selectedDistrict);
  const vendorDistricts    = selectedState ? (DISTRICTS_BY_STATE[selectedState] ?? []) : [];
  const vendorDISCOMs      = resolveDiscomOptions(selectedState, selectedDistrict);

  // State role: Gujarat is the fixed context — both dropdowns are independent (always enabled)
  const stateDistricts = DISTRICTS_BY_STATE['Gujarat'] ?? [];
  const stateDISCOMs   = DISCOMS_BY_STATE['Gujarat'] ?? [];

  return (
    <div className="bg-white border-b border-border px-4 py-2 flex items-center gap-2 flex-wrap min-h-[44px]">

      {/* ── CONSUMER: preset pills ───────────────────────────────── */}
      {isConsumer && (
        <div className="flex items-center gap-1">
          {DATE_PRESETS.map(p => (
            <button
              key={p.key}
              onClick={() => setDatePreset(p.key)}
              className={`text-xs px-2.5 py-1.5 rounded-input border transition-colors ${
                datePreset === p.key
                  ? 'bg-consumer-green text-white border-consumer-green'
                  : 'bg-white text-text-secondary border-border hover:border-consumer-green hover:text-consumer-green'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* ── NATIONAL / STATE / DISCOM / VENDOR: date range ──────── */}
      {!isConsumer && (
        <div className="flex items-center gap-1.5">
          <input type="date" defaultValue="2026-04-01" className={selectCls} />
          <span className="text-text-tertiary text-xs">–</span>
          <input type="date" defaultValue="2026-04-27" className={selectCls} />
        </div>
      )}

      {/* ── NATIONAL: State → District (opt) → DISCOM ───────────── */}
      {isNational && (
        <>
          <select className={selectCls} value={selectedState} onChange={e => setSelectedState(e.target.value)}>
            <option value="">All States</option>
            {STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className={selectCls} value={selectedDistrict} onChange={e => setSelectedDistrict(e.target.value)} disabled={!selectedState}>
            <option value="">All Districts</option>
            {nationalDistricts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          {/* DISCOM enabled after State; narrows if District also selected */}
          <select className={selectCls} value={selectedDISCOM} onChange={e => setSelectedDISCOM(e.target.value)} disabled={!selectedState}>
            <option value="">All DISCOMs</option>
            {nationalDISCOMs.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </>
      )}

      {/* ── STATE: District + DISCOM independent (both always enabled) */}
      {isState && (
        <>
          <select className={selectCls} value={selectedDistrict} onChange={e => setSelectedDistrict(e.target.value)}>
            <option value="">All Districts</option>
            {stateDistricts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select className={selectCls} value={selectedDISCOM} onChange={e => setSelectedDISCOM(e.target.value)}>
            <option value="">All DISCOMs</option>
            {stateDISCOMs.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </>
      )}

      {/* ── DISCOM: Circle → Division → Sub-Division ─────────────── */}
      {isDISCOM && (
        <>
          <select className={selectCls} value={selectedCircle} onChange={e => setSelectedCircle(e.target.value)}>
            <option value="">All Circles</option>
            {CIRCLES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className={selectCls} value={selectedDISCOM} onChange={e => setSelectedDISCOM(e.target.value)} disabled={!selectedCircle}>
            <option value="">All Divisions</option>
            {availableDivisions.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select className={selectCls} value={selectedSubDivision} onChange={e => setSelectedSubDivision(e.target.value)} disabled={!selectedDISCOM}>
            <option value="">All Sub-Divisions</option>
            {availableSubDivisions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </>
      )}

      {/* ── VENDOR: State → District (opt) → DISCOM ─────────────── */}
      {isVendor && (
        <>
          <select className={selectCls} value={selectedState} onChange={e => setSelectedState(e.target.value)}>
            <option value="">All States</option>
            {VENDOR_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className={selectCls} value={selectedDistrict} onChange={e => setSelectedDistrict(e.target.value)} disabled={!selectedState}>
            <option value="">All Districts</option>
            {vendorDistricts.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          {/* DISCOM enabled after State; narrows if District also selected */}
          <select className={selectCls} value={selectedDISCOM} onChange={e => setSelectedDISCOM(e.target.value)} disabled={!selectedState}>
            <option value="">All DISCOMs</option>
            {vendorDISCOMs.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </>
      )}

      <div className="flex-1" />

      {/* ── Export button ─────────────────────────────────────────── */}
      {!isConsumer && (
        <button className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-input border border-border bg-white text-text-secondary hover:text-accent hover:border-accent transition-colors">
          <Download size={12} />
          Export CSV
        </button>
      )}
      {isConsumer && (
        <button className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-input border border-consumer-green bg-white text-consumer-green hover:bg-green-50 transition-colors">
          <Download size={12} />
          Download report
        </button>
      )}
    </div>
  );
}
